import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Map as MapIcon, Plus, Pencil, Trash2, Store as StoreIcon } from "lucide-react";

export default function AdminZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: null, name: "" });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/zones");
      setZones(data);
    } catch { toast.error("Failed to load zones"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ id: null, name: "" }); setOpen(true); };
  const openEdit = (z) => { setForm({ id: z.id, name: z.name }); setOpen(true); };

  const save = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Zone name required");
    setSaving(true);
    try {
      if (form.id) { await api.patch(`/zones/${form.id}`, { name }); toast.success("Zone updated (linked stores renamed)"); }
      else { await api.post("/zones", { name }); toast.success("Zone created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/zones/${confirmDelete.id}`);
      toast.success("Zone deleted"); setConfirmDelete(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const totalStores = zones.reduce((s, z) => s + (z.store_count || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Manage Zones" subtitle="Group stores into operational zones (above city)" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{zones.length}</span> zones · covering{" "}
              <span className="font-semibold text-gray-900">{totalStores}</span> store{totalStores === 1 ? "" : "s"}
            </div>
            <Button data-testid="add-zone-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721]">
              <Plus className="h-4 w-4 mr-2" /> Add Zone
            </Button>
          </div>

          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="zones-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Zone</th>
                    <th className="text-left px-4 py-3">Stores</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={3} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                  {!loading && zones.map(z => (
                    <tr key={z.id} className="border-t border-gray-100" data-testid={`zone-row-${z.name}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <MapIcon className="h-4 w-4 text-brand-red" />
                          <span className="font-semibold">{z.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100 border-0">
                          <StoreIcon className="h-3 w-3 mr-1" /> {z.store_count || 0}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button data-testid={`edit-zone-${z.name}`} size="sm" variant="outline" onClick={() => openEdit(z)} className="mr-1">
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Rename
                        </Button>
                        <Button data-testid={`delete-zone-${z.name}`} size="sm" variant="outline"
                          onClick={() => setConfirmDelete(z)}
                          className="text-red-600 border-red-200 hover:bg-red-50">
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!loading && zones.length === 0 && (
                    <tr><td colSpan={3} className="text-center py-8 text-gray-500">No zones yet. Create your first one.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="text-xs text-gray-500">
            Tip: renaming a zone automatically updates all linked stores. Deletion is only allowed if the zone has no stores assigned.
          </div>
        </main>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="zone-form-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>{form.id ? "Rename zone" : "Add zone"}</DialogTitle></DialogHeader>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Zone name</label>
            <Input data-testid="zone-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Central UP" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-zone-btn" onClick={save} disabled={saving} className="bg-brand-red hover:bg-[#B31721]">
              {form.id ? "Save" : "Add zone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="zone-delete-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete zone?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">
            Delete <span className="font-semibold">{confirmDelete?.name}</span>? This is only allowed when no stores are assigned.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button data-testid="confirm-delete-zone-btn" onClick={del} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
